import { query_client } from '$tablet-app/src/app/query_client';
import { createAlarmModel } from '@akin-home/alarms';
import { createAlertModel } from '@akin-home/alerts';
import { createCalendarItem } from '@akin-home/calendar/combined';
import { getHabitatID } from '@akin-home/habitats/react';
import {
  Recurrence,
  getFrequencyFromMode,
  processWeekdays,
} from '@akin-home/recurrence';
import { apiQueryKeys } from '../../../calendar/combined/getCalendarItemAPIFunctions';
import { createEventModel } from '../../../models';
import { addHours } from '../../../utils/dateTime';
import { AddCalendarItem } from '../types';

export const createCalendarItemCommand = (
  command: AddCalendarItem,
  habitatID: string
) => {
  const result = AddCalendarItem.safeParse(command);

  if (!result.success) {
    console.log('Error: ', result.error);
    return;
  }

  const { data } = result.data;

  const { type } = data;
  const title = data.title || type;
  const { start, end } = parseDates(data);

  const recurrence_data = processRecurrenceData(result.data);

  switch (type) {
    case 'Event':
      return createEventModel({
        start,
        end,
        title,
        habitatID,
        ...recurrence_data,
      });
    case 'Alert':
      return createAlertModel({
        start,
        title,
        habitatID,
        ...recurrence_data,
      });
    case 'Alarm':
      return createAlarmModel({
        start,
        title,
        habitatID,
        ...recurrence_data,
      });
  }
};

/** Handles an add_calendar_item command */
const handleAddCalendarItemCommand = async (command: AddCalendarItem) => {
  const habitatID = getHabitatID() as string; // Get habitatID from state store

  const item = createCalendarItemCommand(command, habitatID);

  if (!item) return;

  await createCalendarItem(item);

  query_client.invalidateQueries(apiQueryKeys[item.type]);
};

/**
 * Parses the dates out of the command data.
 */
const parseDates = ({
  start_date,
  start_time,
  end_date,
  end_time,
}: AddCalendarItem['data']) => {
  /** Get the dates */
  const start = start_date ? new Date(start_date) : new Date();

  /** Set the start time */
  if (start_time) {
    const [hours, mins] = start_time.split(':');

    start.setHours(parseInt(hours));
    start.setMinutes(parseInt(mins));
  }

  const end = end_date ? new Date(end_date) : addHours(start, 1);

  /** Set the end time */
  if (end_time) {
    const [hours, mins] = end_time.split(':');

    end.setHours(parseInt(hours));
    end.setMinutes(parseInt(mins));
  }

  return { start, end };
};

const processRecurrenceData = ({ data }: AddCalendarItem) => {
  const {
    recurrence_days,
    recurrence_interval,
    recurrence_mode,
    recurrence_count,
    recurrence_until,
  } = data;

  if (!recurrence_mode) {
    return;
  }

  const until = recurrence_until ? new Date(recurrence_until) : undefined;

  const recurrence: Recurrence = {
    mode: recurrence_mode,
    freq: getFrequencyFromMode(recurrence_mode),
    interval: recurrence_interval,
    count: recurrence_count,
    until,
    byweekday: recurrence_days ? processWeekdays(recurrence_days) : undefined,
  };

  return {
    recurrence,
    recurrenceUntil: until,
  };
};

export default handleAddCalendarItemCommand;
